import { 
  Megaphone, Target, DollarSign, Image, Settings, 
  Users, Clock, Shield, BarChart3, Globe, 
  Smartphone, Monitor, Tv, Radio, MapPin, Check
} from "lucide-react";

// ==================== STEP CONFIGURATION ====================

export const STEPS = [
  { id: 1, key: "overview", title: "Campaign overview", icon: Megaphone },
  { id: 2, key: "budget", title: "Budget & bidding", icon: DollarSign },
  { id: 3, key: "targeting", title: "Targeting", icon: Target },
  { id: 4, key: "audience", title: "Audience", icon: Users },
  { id: 5, key: "creatives", title: "Creatives", icon: Image },
  { id: 6, key: "schedule", title: "Schedule & pacing", icon: Clock },
  { id: 7, key: "safety", title: "Brand safety", icon: Shield },
  { id: 8, key: "measurement", title: "Measurement", icon: BarChart3 },
];

// ==================== CAMPAIGN GOALS & KPIs ====================

export const CAMPAIGN_GOALS = [
  { value: "brand_awareness", label: "Brand Awareness", desc: "Increase visibility" },
  { value: "reach", label: "Reach", desc: "Maximize unique users" },
  { value: "traffic", label: "Traffic", desc: "Drive website visits" },
  { value: "engagement", label: "Engagement", desc: "Increase interactions" },
  { value: "app_installs", label: "App Installs", desc: "Drive app downloads" },
  { value: "video_views", label: "Video Views", desc: "Maximize video consumption" },
  { value: "lead_generation", label: "Lead Generation", desc: "Collect leads" },
  { value: "conversions", label: "Conversions", desc: "Drive purchases/actions" },
];

export const KPI_TYPES = [
  { value: "cpm", label: "CPM", desc: "Cost per 1,000 impressions" },
  { value: "cpc", label: "CPC", desc: "Cost per click" },
  { value: "cpa", label: "CPA", desc: "Cost per acquisition" },
  { value: "cpv", label: "CPV", desc: "Cost per view" },
  { value: "cpcv", label: "CPCV", desc: "Cost per completed view" },
  { value: "cps", label: "CPS", desc: "Cost per session/visit" },
  { value: "cptv", label: "CPTV", desc: "Cost per true view" },
  { value: "vcpm", label: "vCPM", desc: "Viewable CPM" },
  { value: "roas", label: "ROAS", desc: "Return on ad spend" },
];

// ==================== BIDDING & BUDGET ====================

export const BIDDING_STRATEGIES = [
  { value: "manual_cpm", label: "Manual CPM", desc: "Set your own CPM bid", basis: "cpm" },
  { value: "manual_cpc", label: "Manual CPC", desc: "Set your own CPC bid", basis: "cpc" },
  { value: "target_cpa", label: "Target CPA", desc: "Optimize for conversions", basis: "cpm" },
  { value: "target_roas", label: "Target ROAS", desc: "Optimize for return", basis: "cpm" },
  { value: "target_cpcv", label: "Target CPCV", desc: "Optimize for video completions", basis: "cpm" },
  { value: "target_cps", label: "Target CPS", desc: "Optimize for sessions/visits", basis: "cpm" },
  { value: "maximize_conversions", label: "Maximize Conversions", desc: "Auto-optimize for conversions", basis: "cpm" },
  { value: "maximize_clicks", label: "Maximize Clicks", desc: "Auto-optimize for clicks", basis: "cpm" },
  { value: "maximize_completed_views", label: "Maximize Completed Views", desc: "Auto-optimize for video completions", basis: "cpm" },
];

export const BID_PRICING_TYPES = [
  { value: "cpm", label: "CPM - Cost per 1,000 impressions" },
  { value: "cpc", label: "CPC - Cost per click" },
  { value: "cpa", label: "CPA - Cost per action" },
  { value: "cpv", label: "CPV - Cost per view" },
  { value: "cpcv", label: "CPCV - Cost per completed view" },
  { value: "cps", label: "CPS - Cost per session" },
];

export const CURRENCIES = [
  { value: "USD", label: "$ USD - US Dollar" },
  { value: "AED", label: "AED - UAE Dirham" },
  { value: "INR", label: "Rs INR - Indian Rupee" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
];

export const PACING_TYPES = [
  { value: "even", label: "Even", desc: "Spread evenly throughout the day" },
  { value: "accelerated", label: "Accelerated", desc: "Spend budget as fast as possible" },
  { value: "front_loaded", label: "Front-loaded", desc: "Spend more early in campaign" },
];

// ==================== INVENTORY & ENVIRONMENT ====================

export const INVENTORY_SOURCES = [
  { value: "open_exchange", label: "Open Exchange", icon: Globe, desc: "Programmatic open market" },
  { value: "pmp", label: "Private Marketplace", icon: Shield, desc: "Curated deals" },
  { value: "pg", label: "Programmatic Guaranteed", icon: Check, desc: "Reserved inventory" },
  { value: "youtube", label: "YouTube", icon: Tv, desc: "Video ads on YouTube" },
  { value: "gdn", label: "Google Display Network", icon: Monitor, desc: "Display across Google" },
  { value: "ctv", label: "Connected TV", icon: Tv, desc: "Streaming TV ads" },
  { value: "audio", label: "Audio", icon: Radio, desc: "Podcast & streaming audio" },
];

export const ENVIRONMENTS = [
  { value: "web", label: "Web", icon: Monitor },
  { value: "app", label: "Mobile App", icon: Smartphone },
  { value: "ctv", label: "Connected TV", icon: Tv },
  { value: "dooh", label: "Digital Out-of-Home", icon: MapPin },
];

// ==================== DEMOGRAPHICS ====================

export const AGE_RANGES = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
export const GENDERS = ["male", "female", "unknown"];
export const INCOME_SEGMENTS = ["low", "medium", "high", "affluent"];
export const PARENTAL_STATUSES = ["parent", "not_parent"];

export const LANGUAGES = [
  { code: "en", name: "English" }, { code: "es", name: "Spanish" },
  { code: "fr", name: "French" }, { code: "de", name: "German" },
  { code: "pt", name: "Portuguese" }, { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" }, { code: "ko", name: "Korean" },
  { code: "ar", name: "Arabic" }, { code: "hi", name: "Hindi" },
  { code: "ru", name: "Russian" }, { code: "it", name: "Italian" },
  { code: "nl", name: "Dutch" }, { code: "pl", name: "Polish" },
  { code: "tr", name: "Turkish" }, { code: "vi", name: "Vietnamese" },
  { code: "th", name: "Thai" }, { code: "id", name: "Indonesian" },
  { code: "ms", name: "Malay" }, { code: "tl", name: "Filipino" },
];

// ==================== DEVICE & TECHNICAL ====================

export const DEVICE_TYPES = [
  { value: 4, label: "Phone", icon: Smartphone },
  { value: 5, label: "Tablet", icon: Monitor },
  { value: 2, label: "Desktop", icon: Monitor },
  { value: 3, label: "Connected TV", icon: Tv },
];

export const OS_LIST = [
  { value: "Android", label: "Android" },
  { value: "iOS", label: "iOS" },
  { value: "Windows", label: "Windows" },
  { value: "macOS", label: "macOS" },
  { value: "Linux", label: "Linux" },
];

export const OS_VERSIONS = {
  Android: ["Android 14", "Android 13", "Android 12", "Android 11", "Android 10", "Android 9", "Android 8"],
  iOS: ["iOS 18", "iOS 17", "iOS 16", "iOS 15", "iOS 14", "iOS 13"],
  Windows: ["Windows 11", "Windows 10", "Windows 8", "Windows 7"],
  macOS: ["macOS 14", "macOS 13", "macOS 12", "macOS 11"],
};

export const BROWSERS = [
  "Chrome", "Safari", "Firefox", "Edge", "Opera", 
  "Samsung Internet", "UC Browser", "Brave", "IE", "Other/Unknown"
];

export const CONNECTION_SPEEDS = [
  { value: "2g", label: "2G" },
  { value: "3g", label: "3G" },
  { value: "4g", label: "4G/LTE" },
  { value: "5g", label: "5G" },
  { value: "wifi", label: "WiFi" },
];

// ==================== DEVICE MAKES & MODELS ====================

export const DEVICE_MAKES = [
  { value: "Apple", label: "Apple" },
  { value: "Samsung", label: "Samsung" },
  { value: "Google", label: "Google" },
  { value: "Xiaomi", label: "Xiaomi" },
  { value: "OnePlus", label: "OnePlus" },
  { value: "Oppo", label: "Oppo" },
  { value: "Vivo", label: "Vivo" },
  { value: "Realme", label: "Realme" },
  { value: "Huawei", label: "Huawei" },
  { value: "Motorola", label: "Motorola" },
  { value: "Nokia", label: "Nokia" },
  { value: "Sony", label: "Sony" },
  { value: "LG", label: "LG" },
  { value: "Asus", label: "Asus" },
  { value: "Lenovo", label: "Lenovo" },
  { value: "HP", label: "HP" },
  { value: "Dell", label: "Dell" },
  { value: "Microsoft", label: "Microsoft" },
  { value: "Acer", label: "Acer" },
  { value: "TCL", label: "TCL" },
  { value: "Honor", label: "Honor" },
  { value: "Nothing", label: "Nothing" },
  { value: "iQOO", label: "iQOO" },
  { value: "Poco", label: "Poco" },
];

export const DEVICE_MODELS = {
  Apple: [
    "iPhone 15 Pro Max", "iPhone 15 Pro", "iPhone 15 Plus", "iPhone 15",
    "iPhone 14 Pro Max", "iPhone 14 Pro", "iPhone 14 Plus", "iPhone 14",
    "iPhone 13 Pro Max", "iPhone 13 Pro", "iPhone 13", "iPhone 13 Mini",
    "iPhone 12 Pro Max", "iPhone 12 Pro", "iPhone 12", "iPhone 12 Mini",
    "iPhone SE (3rd Gen)", "iPhone SE (2nd Gen)",
    "iPad Pro 12.9", "iPad Pro 11", "iPad Air", "iPad Mini", "iPad"
  ],
  Samsung: [
    "Galaxy S24 Ultra", "Galaxy S24+", "Galaxy S24",
    "Galaxy S23 Ultra", "Galaxy S23+", "Galaxy S23",
    "Galaxy S22 Ultra", "Galaxy S22+", "Galaxy S22",
    "Galaxy Z Fold 5", "Galaxy Z Flip 5", "Galaxy Z Fold 4", "Galaxy Z Flip 4",
    "Galaxy A54", "Galaxy A34", "Galaxy A24", "Galaxy A14",
    "Galaxy M54", "Galaxy M34", "Galaxy M14",
    "Galaxy Tab S9", "Galaxy Tab S8", "Galaxy Tab A8"
  ],
  Google: [
    "Pixel 8 Pro", "Pixel 8", "Pixel 8a",
    "Pixel 7 Pro", "Pixel 7", "Pixel 7a",
    "Pixel 6 Pro", "Pixel 6", "Pixel 6a",
    "Pixel Fold", "Pixel Tablet"
  ],
  Xiaomi: [
    "14 Ultra", "14 Pro", "14",
    "13 Ultra", "13 Pro", "13", "13T Pro", "13T",
    "Redmi Note 13 Pro+", "Redmi Note 13 Pro", "Redmi Note 13",
    "Redmi 13C", "Redmi 12", "Redmi A3",
    "Pad 6", "Pad 5"
  ],
  OnePlus: [
    "12", "12R", "11", "11R",
    "Open", "Nord 3", "Nord CE 3", "Nord N30"
  ],
  Oppo: [
    "Find X7 Ultra", "Find X7", "Find X6 Pro",
    "Reno 11 Pro", "Reno 11", "Reno 10 Pro+",
    "A79", "A58", "A38"
  ],
  Vivo: [
    "X100 Pro", "X100", "X90 Pro",
    "V30 Pro", "V30", "V29 Pro",
    "Y100", "Y56"
  ],
  Realme: [
    "GT 5 Pro", "GT 5", "GT 3",
    "12 Pro+", "12 Pro", "12",
    "Narzo 60 Pro", "Narzo 60", "C55"
  ],
  Huawei: [
    "Mate 60 Pro", "Mate 60", "P60 Pro", "P60",
    "Nova 12 Pro", "Nova 12"
  ],
  Motorola: [
    "Edge 40 Pro", "Edge 40", "Edge 30 Ultra",
    "Razr 40 Ultra", "Razr 40",
    "G84", "G54", "G34"
  ],
  Nokia: [
    "G42", "G22", "C32", "C22"
  ],
  Sony: [
    "Xperia 1 V", "Xperia 5 V", "Xperia 10 V"
  ],
  Microsoft: [
    "Surface Pro 9", "Surface Pro 8", "Surface Laptop 5",
    "Surface Go 3", "Surface Duo 2"
  ],
  Honor: [
    "Magic 6 Pro", "Magic 6", "Magic V2",
    "90 Pro", "90", "X9b"
  ],
  Nothing: [
    "Phone 2", "Phone 1", "Phone 2a"
  ],
  iQOO: [
    "12", "11", "Neo 9 Pro", "Neo 9", "Z9"
  ],
  Poco: [
    "F6 Pro", "F6", "X6 Pro", "X6", "M6 Pro"
  ]
};

// ==================== GEO TARGETING ====================

export const COUNTRIES = [
  { code: "USA", name: "United States" }, { code: "GBR", name: "United Kingdom" },
  { code: "CAN", name: "Canada" }, { code: "AUS", name: "Australia" },
  { code: "DEU", name: "Germany" }, { code: "FRA", name: "France" },
  { code: "JPN", name: "Japan" }, { code: "IND", name: "India" },
  { code: "BRA", name: "Brazil" }, { code: "MEX", name: "Mexico" },
  { code: "ESP", name: "Spain" }, { code: "ITA", name: "Italy" },
  { code: "NLD", name: "Netherlands" }, { code: "CHE", name: "Switzerland" },
  { code: "SWE", name: "Sweden" }, { code: "NOR", name: "Norway" },
  { code: "DNK", name: "Denmark" }, { code: "FIN", name: "Finland" },
  { code: "BEL", name: "Belgium" }, { code: "AUT", name: "Austria" },
  { code: "POL", name: "Poland" }, { code: "PRT", name: "Portugal" },
  { code: "IRL", name: "Ireland" }, { code: "NZL", name: "New Zealand" },
  { code: "SGP", name: "Singapore" }, { code: "HKG", name: "Hong Kong" },
  { code: "KOR", name: "South Korea" }, { code: "TWN", name: "Taiwan" },
  { code: "MYS", name: "Malaysia" }, { code: "THA", name: "Thailand" },
  { code: "IDN", name: "Indonesia" }, { code: "PHL", name: "Philippines" },
  { code: "VNM", name: "Vietnam" }, { code: "ARE", name: "UAE" },
  { code: "SAU", name: "Saudi Arabia" }, { code: "ISR", name: "Israel" },
  { code: "ZAF", name: "South Africa" }, { code: "EGY", name: "Egypt" },
  { code: "NGA", name: "Nigeria" }, { code: "KEN", name: "Kenya" },
  { code: "ARG", name: "Argentina" }, { code: "CHL", name: "Chile" },
  { code: "COL", name: "Colombia" }, { code: "PER", name: "Peru" },
  { code: "RUS", name: "Russia" }, { code: "TUR", name: "Turkey" },
  { code: "PAK", name: "Pakistan" }, { code: "BGD", name: "Bangladesh" },
  { code: "UKR", name: "Ukraine" }, { code: "CZE", name: "Czech Republic" },
];

export const COUNTRY_STATES = {
  USA: ["California", "Texas", "Florida", "New York", "Illinois", "Pennsylvania", "Ohio", "Georgia", "North Carolina", "Michigan"],
  IND: [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", 
    "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", 
    "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", 
    "Uttar Pradesh", "Uttarakhand", "West Bengal",
    // Union Territories
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", 
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ],
  GBR: ["England", "Scotland", "Wales", "Northern Ireland"],
  CAN: ["Ontario", "Quebec", "British Columbia", "Alberta", "Manitoba", "Saskatchewan"],
  AUS: ["New South Wales", "Victoria", "Queensland", "Western Australia", "South Australia"],
  IDN: [
    "Aceh", "Bali", "Banten", "Bengkulu", "Central Java", "Central Kalimantan",
    "Central Sulawesi", "East Java", "East Kalimantan", "East Nusa Tenggara",
    "Gorontalo", "Jakarta", "Jambi", "Lampung", "Maluku", "North Kalimantan",
    "North Maluku", "North Sulawesi", "North Sumatra", "Papua", "Riau",
    "Riau Islands", "South Kalimantan", "South Sulawesi", "South Sumatra",
    "Southeast Sulawesi", "West Java", "West Kalimantan", "West Nusa Tenggara",
    "West Papua", "West Sulawesi", "West Sumatra", "Yogyakarta"
  ],
  PHL: [
    "Metro Manila", "Cebu", "Davao", "Central Luzon", "Calabarzon",
    "Western Visayas", "Central Visayas", "Northern Mindanao", "Ilocos Region",
    "Bicol Region", "Eastern Visayas", "Zamboanga Peninsula", "Cagayan Valley"
  ],
  MYS: [
    "Johor", "Kedah", "Kelantan", "Kuala Lumpur", "Labuan", "Melaka",
    "Negeri Sembilan", "Pahang", "Penang", "Perak", "Perlis", "Putrajaya",
    "Sabah", "Sarawak", "Selangor", "Terengganu"
  ],
  SGP: ["Central Region", "East Region", "North Region", "North-East Region", "West Region"],
  THA: [
    "Bangkok", "Chiang Mai", "Chonburi", "Nakhon Ratchasima", "Khon Kaen",
    "Songkhla", "Surat Thani", "Udon Thani", "Nonthaburi", "Phuket"
  ],
  VNM: [
    "Ho Chi Minh City", "Hanoi", "Da Nang", "Hai Phong", "Can Tho",
    "Binh Duong", "Dong Nai", "Khanh Hoa", "Quang Ninh", "Thanh Hoa"
  ],
  ARE: ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"],
  SAU: ["Riyadh", "Makkah", "Eastern Province", "Madinah", "Asir", "Jazan", "Qassim", "Tabuk"],
  DEU: ["Bavaria", "North Rhine-Westphalia", "Baden-Württemberg", "Lower Saxony", "Hesse", "Berlin", "Hamburg"],
  FRA: ["Île-de-France", "Auvergne-Rhône-Alpes", "Nouvelle-Aquitaine", "Occitanie", "Hauts-de-France", "Provence-Alpes-Côte d'Azur"],
  JPN: ["Tokyo", "Osaka", "Kanagawa", "Aichi", "Saitama", "Chiba", "Hyogo", "Hokkaido", "Fukuoka"],
  KOR: ["Seoul", "Busan", "Incheon", "Daegu", "Daejeon", "Gwangju", "Gyeonggi", "Gangwon"],
  BRA: ["São Paulo", "Rio de Janeiro", "Minas Gerais", "Bahia", "Paraná", "Rio Grande do Sul", "Pernambuco"],
  MEX: ["Mexico City", "State of Mexico", "Jalisco", "Nuevo León", "Veracruz", "Puebla", "Guanajuato"],
  NGA: ["Lagos", "Kano", "Rivers", "Kaduna", "Oyo", "Katsina", "Anambra", "Ogun"],
  EGY: ["Cairo", "Giza", "Alexandria", "Dakahlia", "Red Sea", "Beheira", "Sharqia"],
  ZAF: ["Gauteng", "KwaZulu-Natal", "Western Cape", "Eastern Cape", "Limpopo", "Mpumalanga"],
};

// India State-wise Cities with Tier Classification
export const INDIA_STATE_CITIES = {
  "Andhra Pradesh": {
    tier1: ["Visakhapatnam", "Vijayawada"],
    tier2: ["Guntur", "Nellore", "Kurnool", "Rajahmundry", "Tirupati", "Kakinada"],
    tier3: ["Kadapa", "Anantapur", "Eluru", "Ongole", "Vizianagaram", "Machilipatnam"]
  },
  "Arunachal Pradesh": {
    tier1: [],
    tier2: ["Itanagar"],
    tier3: ["Naharlagun", "Pasighat", "Tawang"]
  },
  "Assam": {
    tier1: ["Guwahati"],
    tier2: ["Silchar", "Dibrugarh", "Jorhat", "Nagaon"],
    tier3: ["Tezpur", "Tinsukia", "Bongaigaon", "Dhubri"]
  },
  "Bihar": {
    tier1: ["Patna"],
    tier2: ["Gaya", "Bhagalpur", "Muzaffarpur", "Darbhanga"],
    tier3: ["Purnia", "Arrah", "Begusarai", "Katihar", "Munger", "Chhapra"]
  },
  "Chhattisgarh": {
    tier1: ["Raipur"],
    tier2: ["Bhilai", "Bilaspur", "Korba", "Durg"],
    tier3: ["Rajnandgaon", "Raigarh", "Jagdalpur", "Ambikapur"]
  },
  "Goa": {
    tier1: [],
    tier2: ["Panaji", "Margao", "Vasco da Gama"],
    tier3: ["Mapusa", "Ponda", "Bicholim"]
  },
  "Gujarat": {
    tier1: ["Ahmedabad", "Surat", "Vadodara", "Rajkot"],
    tier2: ["Bhavnagar", "Jamnagar", "Junagadh", "Gandhinagar", "Anand"],
    tier3: ["Nadiad", "Morbi", "Mehsana", "Bharuch", "Vapi", "Navsari", "Veraval"]
  },
  "Haryana": {
    tier1: ["Gurugram", "Faridabad"],
    tier2: ["Panipat", "Ambala", "Yamunanagar", "Rohtak", "Hisar", "Karnal"],
    tier3: ["Sonipat", "Panchkula", "Bhiwani", "Sirsa", "Bahadurgarh", "Jind"]
  },
  "Himachal Pradesh": {
    tier1: [],
    tier2: ["Shimla", "Dharamshala", "Solan"],
    tier3: ["Mandi", "Palampur", "Baddi", "Nahan", "Kullu", "Hamirpur"]
  },
  "Jharkhand": {
    tier1: ["Ranchi", "Jamshedpur"],
    tier2: ["Dhanbad", "Bokaro", "Hazaribagh"],
    tier3: ["Deoghar", "Giridih", "Ramgarh", "Phusro", "Medininagar"]
  },
  "Karnataka": {
    tier1: ["Bengaluru", "Mysuru"],
    tier2: ["Hubli-Dharwad", "Mangaluru", "Belagavi", "Davangere", "Ballari"],
    tier3: ["Shimoga", "Tumkur", "Gulbarga", "Udupi", "Hassan", "Bidar", "Raichur"]
  },
  "Kerala": {
    tier1: ["Kochi", "Thiruvananthapuram"],
    tier2: ["Kozhikode", "Thrissur", "Kollam", "Kannur", "Alappuzha"],
    tier3: ["Kottayam", "Palakkad", "Malappuram", "Kasaragod", "Pathanamthitta"]
  },
  "Madhya Pradesh": {
    tier1: ["Indore", "Bhopal"],
    tier2: ["Jabalpur", "Gwalior", "Ujjain", "Sagar"],
    tier3: ["Dewas", "Satna", "Ratlam", "Rewa", "Murwara", "Singrauli", "Burhanpur"]
  },
  "Maharashtra": {
    tier1: ["Mumbai", "Pune", "Nagpur"],
    tier2: ["Nashik", "Aurangabad", "Solapur", "Thane", "Kolhapur", "Navi Mumbai"],
    tier3: ["Amravati", "Nanded", "Sangli", "Malegaon", "Jalgaon", "Akola", "Latur", "Dhule", "Ahmednagar"]
  },
  "Manipur": {
    tier1: [],
    tier2: ["Imphal"],
    tier3: ["Thoubal", "Bishnupur", "Churachandpur"]
  },
  "Meghalaya": {
    tier1: [],
    tier2: ["Shillong"],
    tier3: ["Tura", "Jowai", "Nongstoin"]
  },
  "Mizoram": {
    tier1: [],
    tier2: ["Aizawl"],
    tier3: ["Lunglei", "Champhai", "Serchhip"]
  },
  "Nagaland": {
    tier1: [],
    tier2: ["Dimapur", "Kohima"],
    tier3: ["Mokokchung", "Tuensang", "Wokha"]
  },
  "Odisha": {
    tier1: ["Bhubaneswar"],
    tier2: ["Cuttack", "Rourkela", "Berhampur", "Sambalpur"],
    tier3: ["Puri", "Balasore", "Bhadrak", "Baripada", "Jharsuguda", "Jeypore", "Angul"]
  },
  "Punjab": {
    tier1: ["Ludhiana", "Amritsar"],
    tier2: ["Jalandhar", "Patiala", "Bathinda"],
    tier3: ["Mohali", "Pathankot", "Hoshiarpur", "Moga", "Batala", "Abohar", "Malerkotla"]
  },
  "Rajasthan": {
    tier1: ["Jaipur", "Jodhpur"],
    tier2: ["Kota", "Bikaner", "Ajmer", "Udaipur", "Bhilwara"],
    tier3: ["Alwar", "Bharatpur", "Sikar", "Pali", "Sri Ganganagar", "Beawar", "Kishangarh"]
  },
  "Sikkim": {
    tier1: [],
    tier2: ["Gangtok"],
    tier3: ["Namchi", "Gyalshing", "Mangan"]
  },
  "Tamil Nadu": {
    tier1: ["Chennai", "Coimbatore", "Madurai"],
    tier2: ["Tiruchirappalli", "Salem", "Tirunelveli", "Tiruppur", "Vellore"],
    tier3: ["Erode", "Thoothukudi", "Dindigul", "Thanjavur", "Ranipet", "Sivakasi", "Karur", "Hosur"]
  },
  "Telangana": {
    tier1: ["Hyderabad"],
    tier2: ["Warangal", "Nizamabad", "Karimnagar", "Khammam"],
    tier3: ["Ramagundam", "Mahbubnagar", "Nalgonda", "Adilabad", "Suryapet", "Miryalaguda"]
  },
  "Tripura": {
    tier1: [],
    tier2: ["Agartala"],
    tier3: ["Udaipur", "Dharmanagar", "Kailashahar"]
  },
  "Uttar Pradesh": {
    tier1: ["Lucknow", "Kanpur", "Noida", "Ghaziabad"],
    tier2: ["Agra", "Varanasi", "Meerut", "Allahabad", "Bareilly", "Aligarh", "Moradabad"],
    tier3: ["Gorakhpur", "Saharanpur", "Jhansi", "Firozabad", "Muzaffarnagar", "Mathura", "Rampur", "Shahjahanpur"]
  },
  "Uttarakhand": {
    tier1: ["Dehradun"],
    tier2: ["Haridwar", "Roorkee", "Haldwani"],
    tier3: ["Rudrapur", "Kashipur", "Rishikesh", "Nainital", "Mussoorie"]
  },
  "West Bengal": {
    tier1: ["Kolkata"],
    tier2: ["Asansol", "Siliguri", "Durgapur", "Howrah"],
    tier3: ["Bardhaman", "Malda", "Baharampur", "Habra", "Kharagpur", "Shantipur", "Dankuni"]
  },
  "Delhi": {
    tier1: ["New Delhi", "Delhi"],
    tier2: [],
    tier3: []
  },
  "Chandigarh": {
    tier1: ["Chandigarh"],
    tier2: [],
    tier3: []
  },
  "Puducherry": {
    tier1: [],
    tier2: ["Puducherry"],
    tier3: ["Karaikal", "Mahe", "Yanam"]
  },
  "Jammu and Kashmir": {
    tier1: [],
    tier2: ["Srinagar", "Jammu"],
    tier3: ["Anantnag", "Baramulla", "Sopore", "Kathua", "Udhampur"]
  },
  "Ladakh": {
    tier1: [],
    tier2: ["Leh"],
    tier3: ["Kargil"]
  },
  "Andaman and Nicobar Islands": {
    tier1: [],
    tier2: ["Port Blair"],
    tier3: []
  },
  "Dadra and Nagar Haveli and Daman and Diu": {
    tier1: [],
    tier2: ["Silvassa", "Daman"],
    tier3: ["Diu"]
  },
  "Lakshadweep": {
    tier1: [],
    tier2: ["Kavaratti"],
    tier3: []
  }
};

export const COUNTRY_CITIES = {
  USA: ["New York", "Los Angeles", "Chicago", "Houston", "Phoenix", "Philadelphia", "San Antonio", "San Diego", "Dallas", "San Jose"],
  IND: ["Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad", "Jaipur", "Surat", "Visakhapatnam"],
  GBR: ["London", "Birmingham", "Manchester", "Glasgow", "Liverpool", "Leeds", "Sheffield", "Edinburgh"],
  CAN: ["Toronto", "Montreal", "Vancouver", "Calgary", "Edmonton", "Ottawa"],
  AUS: ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide"],
  IDN: [
    "Jakarta", "Surabaya", "Bandung", "Medan", "Semarang", "Makassar", "Palembang",
    "Tangerang", "Depok", "Bekasi", "Batam", "Bogor", "Pekanbaru", "Bandar Lampung",
    "Malang", "Padang", "Denpasar", "Samarinda", "Banjarmasin", "Pontianak",
    "Manado", "Balikpapan", "Jambi", "Mataram", "Yogyakarta", "Cirebon",
    "Solo", "Ambon", "Kupang", "Jayapura", "Sorong", "Ternate"
  ],
  PHL: [
    "Manila", "Quezon City", "Davao City", "Cebu City", "Zamboanga City",
    "Antipolo", "Taguig", "Pasig", "Cagayan de Oro", "Makati", "Caloocan",
    "Bacolod", "General Santos", "Iloilo City", "Lapu-Lapu"
  ],
  MYS: [
    "Kuala Lumpur", "George Town", "Johor Bahru", "Ipoh", "Shah Alam",
    "Petaling Jaya", "Kota Kinabalu", "Kuching", "Melaka", "Alor Setar",
    "Miri", "Seremban", "Kuantan", "Kota Bharu"
  ],
  SGP: ["Singapore"],
  THA: [
    "Bangkok", "Chiang Mai", "Pattaya", "Nonthaburi", "Udon Thani",
    "Chonburi", "Nakhon Ratchasima", "Hat Yai", "Pak Kret", "Khon Kaen", "Phuket"
  ],
  VNM: [
    "Ho Chi Minh City", "Hanoi", "Da Nang", "Hai Phong", "Can Tho",
    "Bien Hoa", "Nha Trang", "Hue", "Buon Ma Thuot", "Vung Tau"
  ],
  ARE: ["Dubai", "Abu Dhabi", "Sharjah", "Al Ain", "Ajman", "Ras Al Khaimah", "Fujairah"],
  SAU: ["Riyadh", "Jeddah", "Mecca", "Medina", "Dammam", "Khobar", "Tabuk", "Buraydah"],
  DEU: ["Berlin", "Hamburg", "Munich", "Cologne", "Frankfurt", "Stuttgart", "Düsseldorf", "Leipzig"],
  FRA: ["Paris", "Marseille", "Lyon", "Toulouse", "Nice", "Nantes", "Strasbourg", "Bordeaux"],
  JPN: ["Tokyo", "Yokohama", "Osaka", "Nagoya", "Sapporo", "Fukuoka", "Kobe", "Kyoto", "Kawasaki"],
  KOR: ["Seoul", "Busan", "Incheon", "Daegu", "Daejeon", "Gwangju", "Suwon", "Ulsan"],
  BRA: ["São Paulo", "Rio de Janeiro", "Brasília", "Salvador", "Fortaleza", "Belo Horizonte", "Manaus", "Curitiba"],
  MEX: ["Mexico City", "Guadalajara", "Monterrey", "Puebla", "Tijuana", "León", "Zapopan", "Juárez"],
  NGA: ["Lagos", "Kano", "Ibadan", "Abuja", "Port Harcourt", "Benin City", "Kaduna", "Enugu"],
  EGY: ["Cairo", "Alexandria", "Giza", "Shubra El Kheima", "Port Said", "Suez", "Luxor", "Aswan"],
  ZAF: ["Johannesburg", "Cape Town", "Durban", "Pretoria", "Port Elizabeth", "Bloemfontein", "Nelspruit"],
};

export const TELECOM_OPERATORS = {
  USA: ["AT&T", "Verizon", "T-Mobile", "Sprint", "US Cellular"],
  IND: ["Jio", "Airtel", "Vi", "BSNL"],
  GBR: ["EE", "O2", "Vodafone", "Three"],
};

// ==================== AUDIENCE SEGMENTS ====================

export const AFFINITY_SEGMENTS = [
  "Tech Enthusiasts", "Auto Enthusiasts", "Sports Fans", "Fashion Lovers",
  "Foodies", "Travel Buffs", "Music Lovers", "Movie Buffs", "Gamers",
  "Home Improvement", "Pet Lovers", "Fitness Enthusiasts", "Beauty & Wellness",
  "News Junkies", "Business Professionals"
];

export const IN_MARKET_SEGMENTS = [
  "Autos & Vehicles", "Real Estate", "Financial Services", "Travel",
  "Consumer Electronics", "Apparel & Accessories", "Beauty & Personal Care",
  "Home & Garden", "Software", "Telecommunications", "Education",
  "Employment", "Dating Services", "Baby & Children's Products",
  "Business Services", "Legal Services"
];

// ==================== SUPPLY SOURCES ====================

export const SUPPLY_SOURCES = [
  "Google AdX", "Xandr", "Magnite", "PubMatic", "OpenX",
  "Index Exchange", "TripleLift", "Sovrn", "GumGum", "33Across",
  "Sharethrough", "Media.net", "Criteo", "Taboola"
];

// ==================== PLACEMENT & VIEWABILITY ====================

export const AD_PLACEMENTS_DISPLAY = [
  { value: "above_fold", label: "Above the Fold" },
  { value: "below_fold", label: "Below the Fold" },
  { value: "unknown", label: "Unknown" },
];

export const AD_PLACEMENTS_INCONTENT = [
  { value: "in_article", label: "In-article" },
  { value: "in_feed", label: "In-feed" },
  { value: "interstitial", label: "Interstitial" },
  { value: "in_banner", label: "In-banner" },
  { value: "unknown", label: "Unknown" },
];

export const AD_PLACEMENTS_NATIVE = [
  { value: "in_article", label: "In-article" },
  { value: "in_feed", label: "In-feed" },
  { value: "peripheral", label: "Peripheral" },
  { value: "recommendation", label: "Recommendation" },
  { value: "unknown", label: "Unknown" },
];

// ==================== BRAND SAFETY ====================

export const BRAND_SAFETY_LEVELS = [
  { value: "standard", label: "Standard", desc: "Basic safety controls" },
  { value: "strict", label: "Strict", desc: "Enhanced safety controls" },
  { value: "custom", label: "Custom", desc: "Define your own rules" },
];

export const BLOCKED_CATEGORIES = [
  "Adult", "Violence", "Gambling", "Alcohol", "Tobacco",
  "Drugs", "Politics", "Religion", "Hate Speech", "Fake News",
  "Crime", "Death & Injury", "Military Conflict", "Sensitive Social Issues"
];

// ==================== ATTRIBUTION ====================

export const ATTRIBUTION_MODELS = [
  { value: "last_touch", label: "Last Touch", desc: "Credit to last interaction" },
  { value: "first_touch", label: "First Touch", desc: "Credit to first interaction" },
  { value: "linear", label: "Linear", desc: "Equal credit across all touchpoints" },
  { value: "time_decay", label: "Time Decay", desc: "More credit to recent interactions" },
  { value: "position_based", label: "Position Based", desc: "40/20/40 split" },
  { value: "data_driven", label: "Data Driven", desc: "ML-based attribution" },
];

// ==================== IAB CATEGORIES ====================

export const IAB_CATEGORIES = [
  // IAB1 - Arts & Entertainment
  { code: "IAB1", name: "Arts & Entertainment", parent: null },
  { code: "IAB1-1", name: "Books & Literature", parent: "IAB1" },
  { code: "IAB1-2", name: "Celebrity Fan/Gossip", parent: "IAB1" },
  { code: "IAB1-3", name: "Fine Art", parent: "IAB1" },
  { code: "IAB1-4", name: "Humor", parent: "IAB1" },
  { code: "IAB1-5", name: "Movies", parent: "IAB1" },
  { code: "IAB1-6", name: "Music", parent: "IAB1" },
  { code: "IAB1-7", name: "Television", parent: "IAB1" },
  
  // IAB2 - Automotive
  { code: "IAB2", name: "Automotive", parent: null },
  { code: "IAB2-1", name: "Auto Parts", parent: "IAB2" },
  { code: "IAB2-2", name: "Auto Repair", parent: "IAB2" },
  { code: "IAB2-3", name: "Buying/Selling Cars", parent: "IAB2" },
  { code: "IAB2-4", name: "Car Culture", parent: "IAB2" },
  { code: "IAB2-5", name: "Certified Pre-Owned", parent: "IAB2" },
  { code: "IAB2-6", name: "Convertible", parent: "IAB2" },
  { code: "IAB2-7", name: "Coupe", parent: "IAB2" },
  { code: "IAB2-8", name: "Crossover", parent: "IAB2" },
  { code: "IAB2-9", name: "Diesel", parent: "IAB2" },
  { code: "IAB2-10", name: "Electric Vehicle", parent: "IAB2" },
  { code: "IAB2-11", name: "Hatchback", parent: "IAB2" },
  { code: "IAB2-12", name: "Hybrid", parent: "IAB2" },
  { code: "IAB2-13", name: "Luxury", parent: "IAB2" },
  { code: "IAB2-14", name: "Minivan", parent: "IAB2" },
  { code: "IAB2-15", name: "Motorcycles", parent: "IAB2" },
  { code: "IAB2-16", name: "Off-Road Vehicles", parent: "IAB2" },
  { code: "IAB2-17", name: "Performance Vehicles", parent: "IAB2" },
  { code: "IAB2-18", name: "Pickup", parent: "IAB2" },
  { code: "IAB2-19", name: "Road-Side Assistance", parent: "IAB2" },
  { code: "IAB2-20", name: "Sedan", parent: "IAB2" },
  { code: "IAB2-21", name: "Trucks & Accessories", parent: "IAB2" },
  { code: "IAB2-22", name: "Vintage Cars", parent: "IAB2" },
  { code: "IAB2-23", name: "Wagon", parent: "IAB2" },
  
  // IAB3 - Business
  { code: "IAB3", name: "Business", parent: null },
  { code: "IAB3-1", name: "Advertising", parent: "IAB3" },
  { code: "IAB3-2", name: "Agriculture", parent: "IAB3" },
  { code: "IAB3-3", name: "Biotech/Biomedical", parent: "IAB3" },
  { code: "IAB3-4", name: "Business Software", parent: "IAB3" },
  { code: "IAB3-5", name: "Construction", parent: "IAB3" },
  { code: "IAB3-6", name: "Forestry", parent: "IAB3" },
  { code: "IAB3-7", name: "Government", parent: "IAB3" },
  { code: "IAB3-8", name: "Green Solutions", parent: "IAB3" },
  { code: "IAB3-9", name: "Human Resources", parent: "IAB3" },
  { code: "IAB3-10", name: "Logistics", parent: "IAB3" },
  { code: "IAB3-11", name: "Marketing", parent: "IAB3" },
  { code: "IAB3-12", name: "Metals", parent: "IAB3" },
  
  // IAB4 - Careers
  { code: "IAB4", name: "Careers", parent: null },
  { code: "IAB4-1", name: "Career Planning", parent: "IAB4" },
  { code: "IAB4-2", name: "College", parent: "IAB4" },
  { code: "IAB4-3", name: "Financial Aid", parent: "IAB4" },
  { code: "IAB4-4", name: "Job Fairs", parent: "IAB4" },
  { code: "IAB4-5", name: "Job Search", parent: "IAB4" },
  { code: "IAB4-6", name: "Resume Writing/Advice", parent: "IAB4" },
  { code: "IAB4-7", name: "Nursing", parent: "IAB4" },
  { code: "IAB4-8", name: "Scholarships", parent: "IAB4" },
  { code: "IAB4-9", name: "Telecommuting", parent: "IAB4" },
  { code: "IAB4-10", name: "U.S. Military", parent: "IAB4" },
  { code: "IAB4-11", name: "Career Advice", parent: "IAB4" },
  
  // IAB5 - Education
  { code: "IAB5", name: "Education", parent: null },
  { code: "IAB5-1", name: "7-12 Education", parent: "IAB5" },
  { code: "IAB5-2", name: "Adult Education", parent: "IAB5" },
  { code: "IAB5-3", name: "Art History", parent: "IAB5" },
  { code: "IAB5-4", name: "College Administration", parent: "IAB5" },
  { code: "IAB5-5", name: "College Life", parent: "IAB5" },
  { code: "IAB5-6", name: "Distance Learning", parent: "IAB5" },
  { code: "IAB5-7", name: "English as a 2nd Language", parent: "IAB5" },
  { code: "IAB5-8", name: "Language Learning", parent: "IAB5" },
  { code: "IAB5-9", name: "Graduate School", parent: "IAB5" },
  { code: "IAB5-10", name: "Homeschooling", parent: "IAB5" },
  { code: "IAB5-11", name: "Homework/Study Tips", parent: "IAB5" },
  { code: "IAB5-12", name: "K-6 Educators", parent: "IAB5" },
  { code: "IAB5-13", name: "Private School", parent: "IAB5" },
  { code: "IAB5-14", name: "Special Education", parent: "IAB5" },
  { code: "IAB5-15", name: "Studying Business", parent: "IAB5" },
  
  // IAB6 - Family & Parenting
  { code: "IAB6", name: "Family & Parenting", parent: null },
  { code: "IAB6-1", name: "Adoption", parent: "IAB6" },
  { code: "IAB6-2", name: "Babies & Toddlers", parent: "IAB6" },
  { code: "IAB6-3", name: "Daycare/Pre School", parent: "IAB6" },
  { code: "IAB6-4", name: "Family Internet", parent: "IAB6" },
  { code: "IAB6-5", name: "Parenting - K-6 Kids", parent: "IAB6" },
  { code: "IAB6-6", name: "Parenting teens", parent: "IAB6" },
  { code: "IAB6-7", name: "Pregnancy", parent: "IAB6" },
  { code: "IAB6-8", name: "Special Needs Kids", parent: "IAB6" },
  { code: "IAB6-9", name: "Eldercare", parent: "IAB6" },
  
  // IAB7 - Health & Fitness
  { code: "IAB7", name: "Health & Fitness", parent: null },
  { code: "IAB7-1", name: "Exercise", parent: "IAB7" },
  { code: "IAB7-2", name: "ADD", parent: "IAB7" },
  { code: "IAB7-3", name: "AIDS/HIV", parent: "IAB7" },
  { code: "IAB7-4", name: "Allergies", parent: "IAB7" },
  { code: "IAB7-5", name: "Alternative Medicine", parent: "IAB7" },
  { code: "IAB7-6", name: "Arthritis", parent: "IAB7" },
  { code: "IAB7-7", name: "Asthma", parent: "IAB7" },
  { code: "IAB7-8", name: "Autism/PDD", parent: "IAB7" },
  { code: "IAB7-9", name: "Bipolar Disorder", parent: "IAB7" },
  { code: "IAB7-10", name: "Brain Tumor", parent: "IAB7" },
  { code: "IAB7-11", name: "Cancer", parent: "IAB7" },
  { code: "IAB7-12", name: "Cholesterol", parent: "IAB7" },
  { code: "IAB7-13", name: "Chronic Fatigue Syndrome", parent: "IAB7" },
  { code: "IAB7-14", name: "Chronic Pain", parent: "IAB7" },
  { code: "IAB7-15", name: "Cold & Flu", parent: "IAB7" },
  { code: "IAB7-16", name: "Deafness", parent: "IAB7" },
  { code: "IAB7-17", name: "Dental Care", parent: "IAB7" },
  { code: "IAB7-18", name: "Depression", parent: "IAB7" },
  { code: "IAB7-19", name: "Dermatology", parent: "IAB7" },
  { code: "IAB7-20", name: "Diabetes", parent: "IAB7" },
  { code: "IAB7-21", name: "Epilepsy", parent: "IAB7" },
  { code: "IAB7-22", name: "GERD/Acid Reflux", parent: "IAB7" },
  { code: "IAB7-23", name: "Headaches/Migraines", parent: "IAB7" },
  { code: "IAB7-24", name: "Heart Disease", parent: "IAB7" },
  { code: "IAB7-25", name: "Herbs for Health", parent: "IAB7" },
  { code: "IAB7-26", name: "Holistic Healing", parent: "IAB7" },
  { code: "IAB7-27", name: "IBS/Crohn's Disease", parent: "IAB7" },
  { code: "IAB7-28", name: "Incest/Abuse Support", parent: "IAB7" },
  { code: "IAB7-29", name: "Incontinence", parent: "IAB7" },
  { code: "IAB7-30", name: "Infertility", parent: "IAB7" },
  { code: "IAB7-31", name: "Men's Health", parent: "IAB7" },
  { code: "IAB7-32", name: "Nutrition", parent: "IAB7" },
  { code: "IAB7-33", name: "Orthopedics", parent: "IAB7" },
  { code: "IAB7-34", name: "Panic/Anxiety Disorders", parent: "IAB7" },
  { code: "IAB7-35", name: "Pediatrics", parent: "IAB7" },
  { code: "IAB7-36", name: "Physical Therapy", parent: "IAB7" },
  { code: "IAB7-37", name: "Psychology/Psychiatry", parent: "IAB7" },
  { code: "IAB7-38", name: "Senior Health", parent: "IAB7" },
  { code: "IAB7-39", name: "Sexuality", parent: "IAB7" },
  { code: "IAB7-40", name: "Sleep Disorders", parent: "IAB7" },
  { code: "IAB7-41", name: "Smoking Cessation", parent: "IAB7" },
  { code: "IAB7-42", name: "Substance Abuse", parent: "IAB7" },
  { code: "IAB7-43", name: "Thyroid Disease", parent: "IAB7" },
  { code: "IAB7-44", name: "Weight Loss", parent: "IAB7" },
  { code: "IAB7-45", name: "Women's Health", parent: "IAB7" },
  
  // IAB8 - Food & Drink
  { code: "IAB8", name: "Food & Drink", parent: null },
  { code: "IAB8-1", name: "American Cuisine", parent: "IAB8" },
  { code: "IAB8-2", name: "Barbecues & Grilling", parent: "IAB8" },
  { code: "IAB8-3", name: "Cajun/Creole", parent: "IAB8" },
  { code: "IAB8-4", name: "Chinese Cuisine", parent: "IAB8" },
  { code: "IAB8-5", name: "Cocktails/Beer", parent: "IAB8" },
  { code: "IAB8-6", name: "Coffee/Tea", parent: "IAB8" },
  { code: "IAB8-7", name: "Cuisine-Specific", parent: "IAB8" },
  { code: "IAB8-8", name: "Desserts & Baking", parent: "IAB8" },
  { code: "IAB8-9", name: "Dining Out", parent: "IAB8" },
  { code: "IAB8-10", name: "Food Allergies", parent: "IAB8" },
  { code: "IAB8-11", name: "French Cuisine", parent: "IAB8" },
  { code: "IAB8-12", name: "Health/Low-Fat Cooking", parent: "IAB8" },
  { code: "IAB8-13", name: "Italian Cuisine", parent: "IAB8" },
  { code: "IAB8-14", name: "Japanese Cuisine", parent: "IAB8" },
  { code: "IAB8-15", name: "Mexican Cuisine", parent: "IAB8" },
  { code: "IAB8-16", name: "Vegan", parent: "IAB8" },
  { code: "IAB8-17", name: "Vegetarian", parent: "IAB8" },
  { code: "IAB8-18", name: "Wine", parent: "IAB8" },
  
  // IAB9 - Hobbies & Interests
  { code: "IAB9", name: "Hobbies & Interests", parent: null },
  { code: "IAB9-1", name: "Art/Technology", parent: "IAB9" },
  { code: "IAB9-2", name: "Arts & Crafts", parent: "IAB9" },
  { code: "IAB9-3", name: "Beadwork", parent: "IAB9" },
  { code: "IAB9-4", name: "Bird-Watching", parent: "IAB9" },
  { code: "IAB9-5", name: "Board Games/Puzzles", parent: "IAB9" },
  { code: "IAB9-6", name: "Candle & Soap Making", parent: "IAB9" },
  { code: "IAB9-7", name: "Card Games", parent: "IAB9" },
  { code: "IAB9-8", name: "Chess", parent: "IAB9" },
  { code: "IAB9-9", name: "Cigars", parent: "IAB9" },
  { code: "IAB9-10", name: "Collecting", parent: "IAB9" },
  { code: "IAB9-11", name: "Comic Books", parent: "IAB9" },
  { code: "IAB9-12", name: "Drawing/Sketching", parent: "IAB9" },
  { code: "IAB9-13", name: "Freelance Writing", parent: "IAB9" },
  { code: "IAB9-14", name: "Genealogy", parent: "IAB9" },
  { code: "IAB9-15", name: "Getting Published", parent: "IAB9" },
  { code: "IAB9-16", name: "Guitar", parent: "IAB9" },
  { code: "IAB9-17", name: "Home Recording", parent: "IAB9" },
  { code: "IAB9-18", name: "Investors & Patents", parent: "IAB9" },
  { code: "IAB9-19", name: "Jewelry Making", parent: "IAB9" },
  { code: "IAB9-20", name: "Magic & Illusion", parent: "IAB9" },
  { code: "IAB9-21", name: "Needlework", parent: "IAB9" },
  { code: "IAB9-22", name: "Painting", parent: "IAB9" },
  { code: "IAB9-23", name: "Photography", parent: "IAB9" },
  { code: "IAB9-24", name: "Radio", parent: "IAB9" },
  { code: "IAB9-25", name: "Roleplaying Games", parent: "IAB9" },
  { code: "IAB9-26", name: "Sci-Fi & Fantasy", parent: "IAB9" },
  { code: "IAB9-27", name: "Scrapbooking", parent: "IAB9" },
  { code: "IAB9-28", name: "Screenwriting", parent: "IAB9" },
  { code: "IAB9-29", name: "Stamps & Coins", parent: "IAB9" },
  { code: "IAB9-30", name: "Video & Computer Games", parent: "IAB9" },
  { code: "IAB9-31", name: "Woodworking", parent: "IAB9" },
  
  // IAB10 - Home & Garden
  { code: "IAB10", name: "Home & Garden", parent: null },
  { code: "IAB10-1", name: "Appliances", parent: "IAB10" },
  { code: "IAB10-2", name: "Entertaining", parent: "IAB10" },
  { code: "IAB10-3", name: "Environmental Safety", parent: "IAB10" },
  { code: "IAB10-4", name: "Gardening", parent: "IAB10" },
  { code: "IAB10-5", name: "Home Repair", parent: "IAB10" },
  { code: "IAB10-6", name: "Home Theater", parent: "IAB10" },
  { code: "IAB10-7", name: "Interior Decorating", parent: "IAB10" },
  { code: "IAB10-8", name: "Landscaping", parent: "IAB10" },
  { code: "IAB10-9", name: "Remodeling & Construction", parent: "IAB10" },
  
  // IAB11 - Law, Government & Politics
  { code: "IAB11", name: "Law, Gov't & Politics", parent: null },
  { code: "IAB11-1", name: "Immigration", parent: "IAB11" },
  { code: "IAB11-2", name: "Legal Issues", parent: "IAB11" },
  { code: "IAB11-3", name: "U.S. Government Resources", parent: "IAB11" },
  { code: "IAB11-4", name: "Politics", parent: "IAB11" },
  { code: "IAB11-5", name: "Commentary", parent: "IAB11" },
  
  // IAB12 - News
  { code: "IAB12", name: "News", parent: null },
  { code: "IAB12-1", name: "International News", parent: "IAB12" },
  { code: "IAB12-2", name: "National News", parent: "IAB12" },
  { code: "IAB12-3", name: "Local News", parent: "IAB12" },
  
  // IAB13 - Personal Finance
  { code: "IAB13", name: "Personal Finance", parent: null },
  { code: "IAB13-1", name: "Beginning Investing", parent: "IAB13" },
  { code: "IAB13-2", name: "Credit/Debt & Loans", parent: "IAB13" },
  { code: "IAB13-3", name: "Financial News", parent: "IAB13" },
  { code: "IAB13-4", name: "Financial Planning", parent: "IAB13" },
  { code: "IAB13-5", name: "Hedge Fund", parent: "IAB13" },
  { code: "IAB13-6", name: "Insurance", parent: "IAB13" },
  { code: "IAB13-7", name: "Investing", parent: "IAB13" },
  { code: "IAB13-8", name: "Mutual Funds", parent: "IAB13" },
  { code: "IAB13-9", name: "Options", parent: "IAB13" },
  { code: "IAB13-10", name: "Retirement Planning", parent: "IAB13" },
  { code: "IAB13-11", name: "Stocks", parent: "IAB13" },
  { code: "IAB13-12", name: "Tax Planning", parent: "IAB13" },
  
  // IAB14 - Society
  { code: "IAB14", name: "Society", parent: null },
  { code: "IAB14-1", name: "Dating", parent: "IAB14" },
  { code: "IAB14-2", name: "Divorce Support", parent: "IAB14" },
  { code: "IAB14-3", name: "Gay Life", parent: "IAB14" },
  { code: "IAB14-4", name: "Marriage", parent: "IAB14" },
  { code: "IAB14-5", name: "Senior Living", parent: "IAB14" },
  { code: "IAB14-6", name: "Teens", parent: "IAB14" },
  { code: "IAB14-7", name: "Weddings", parent: "IAB14" },
  { code: "IAB14-8", name: "Ethnic Specific", parent: "IAB14" },
  
  // IAB15 - Science
  { code: "IAB15", name: "Science", parent: null },
  { code: "IAB15-1", name: "Astrology", parent: "IAB15" },
  { code: "IAB15-2", name: "Biology", parent: "IAB15" },
  { code: "IAB15-3", name: "Chemistry", parent: "IAB15" },
  { code: "IAB15-4", name: "Geology", parent: "IAB15" },
  { code: "IAB15-5", name: "Paranormal Phenomena", parent: "IAB15" },
  { code: "IAB15-6", name: "Physics", parent: "IAB15" },
  { code: "IAB15-7", name: "Space/Astronomy", parent: "IAB15" },
  { code: "IAB15-8", name: "Geography", parent: "IAB15" },
  { code: "IAB15-9", name: "Botany", parent: "IAB15" },
  { code: "IAB15-10", name: "Weather", parent: "IAB15" },
  
  // IAB16 - Pets
  { code: "IAB16", name: "Pets", parent: null },
  { code: "IAB16-1", name: "Aquariums", parent: "IAB16" },
  { code: "IAB16-2", name: "Birds", parent: "IAB16" },
  { code: "IAB16-3", name: "Cats", parent: "IAB16" },
  { code: "IAB16-4", name: "Dogs", parent: "IAB16" },
  { code: "IAB16-5", name: "Large Animals", parent: "IAB16" },
  { code: "IAB16-6", name: "Reptiles", parent: "IAB16" },
  { code: "IAB16-7", name: "Veterinary Medicine", parent: "IAB16" },
  
  // IAB17 - Sports
  { code: "IAB17", name: "Sports", parent: null },
  { code: "IAB17-1", name: "Auto Racing", parent: "IAB17" },
  { code: "IAB17-2", name: "Baseball", parent: "IAB17" },
  { code: "IAB17-3", name: "Bicycling", parent: "IAB17" },
  { code: "IAB17-4", name: "Bodybuilding", parent: "IAB17" },
  { code: "IAB17-5", name: "Boxing", parent: "IAB17" },
  { code: "IAB17-6", name: "Canoeing/Kayaking", parent: "IAB17" },
  { code: "IAB17-7", name: "Cheerleading", parent: "IAB17" },
  { code: "IAB17-8", name: "Climbing", parent: "IAB17" },
  { code: "IAB17-9", name: "Cricket", parent: "IAB17" },
  { code: "IAB17-10", name: "Figure Skating", parent: "IAB17" },
  { code: "IAB17-11", name: "Fly Fishing", parent: "IAB17" },
  { code: "IAB17-12", name: "Football", parent: "IAB17" },
  { code: "IAB17-13", name: "Freshwater Fishing", parent: "IAB17" },
  { code: "IAB17-14", name: "Game & Fish", parent: "IAB17" },
  { code: "IAB17-15", name: "Golf", parent: "IAB17" },
  { code: "IAB17-16", name: "Horse Racing", parent: "IAB17" },
  { code: "IAB17-17", name: "Horses", parent: "IAB17" },
  { code: "IAB17-18", name: "Hunting/Shooting", parent: "IAB17" },
  { code: "IAB17-19", name: "Inline Skating", parent: "IAB17" },
  { code: "IAB17-20", name: "Martial Arts", parent: "IAB17" },
  { code: "IAB17-21", name: "Mountain Biking", parent: "IAB17" },
  { code: "IAB17-22", name: "NASCAR Racing", parent: "IAB17" },
  { code: "IAB17-23", name: "Olympics", parent: "IAB17" },
  { code: "IAB17-24", name: "Paintball", parent: "IAB17" },
  { code: "IAB17-25", name: "Power & Motorcycles", parent: "IAB17" },
  { code: "IAB17-26", name: "Pro Basketball", parent: "IAB17" },
  { code: "IAB17-27", name: "Pro Ice Hockey", parent: "IAB17" },
  { code: "IAB17-28", name: "Rodeo", parent: "IAB17" },
  { code: "IAB17-29", name: "Rugby", parent: "IAB17" },
  { code: "IAB17-30", name: "Running/Jogging", parent: "IAB17" },
  { code: "IAB17-31", name: "Sailing", parent: "IAB17" },
  { code: "IAB17-32", name: "Saltwater Fishing", parent: "IAB17" },
  { code: "IAB17-33", name: "Scuba Diving", parent: "IAB17" },
  { code: "IAB17-34", name: "Skateboarding", parent: "IAB17" },
  { code: "IAB17-35", name: "Skiing", parent: "IAB17" },
  { code: "IAB17-36", name: "Snowboarding", parent: "IAB17" },
  { code: "IAB17-37", name: "Surfing/Body-Boarding", parent: "IAB17" },
  { code: "IAB17-38", name: "Swimming", parent: "IAB17" },
  { code: "IAB17-39", name: "Table Tennis/Ping-Pong", parent: "IAB17" },
  { code: "IAB17-40", name: "Tennis", parent: "IAB17" },
  { code: "IAB17-41", name: "Volleyball", parent: "IAB17" },
  { code: "IAB17-42", name: "Walking", parent: "IAB17" },
  { code: "IAB17-43", name: "Waterski/Wakeboard", parent: "IAB17" },
  { code: "IAB17-44", name: "World Soccer", parent: "IAB17" },
  
  // IAB18 - Style & Fashion
  { code: "IAB18", name: "Style & Fashion", parent: null },
  { code: "IAB18-1", name: "Beauty", parent: "IAB18" },
  { code: "IAB18-2", name: "Body Art", parent: "IAB18" },
  { code: "IAB18-3", name: "Fashion", parent: "IAB18" },
  { code: "IAB18-4", name: "Jewelry", parent: "IAB18" },
  { code: "IAB18-5", name: "Clothing", parent: "IAB18" },
  { code: "IAB18-6", name: "Accessories", parent: "IAB18" },
  
  // IAB19 - Technology & Computing
  { code: "IAB19", name: "Technology & Computing", parent: null },
  { code: "IAB19-1", name: "3-D Graphics", parent: "IAB19" },
  { code: "IAB19-2", name: "Animation", parent: "IAB19" },
  { code: "IAB19-3", name: "Antivirus Software", parent: "IAB19" },
  { code: "IAB19-4", name: "C/C++", parent: "IAB19" },
  { code: "IAB19-5", name: "Cameras & Camcorders", parent: "IAB19" },
  { code: "IAB19-6", name: "Cell Phones", parent: "IAB19" },
  { code: "IAB19-7", name: "Computer Certification", parent: "IAB19" },
  { code: "IAB19-8", name: "Computer Networking", parent: "IAB19" },
  { code: "IAB19-9", name: "Computer Peripherals", parent: "IAB19" },
  { code: "IAB19-10", name: "Computer Reviews", parent: "IAB19" },
  { code: "IAB19-11", name: "Data Centers", parent: "IAB19" },
  { code: "IAB19-12", name: "Databases", parent: "IAB19" },
  { code: "IAB19-13", name: "Desktop Publishing", parent: "IAB19" },
  { code: "IAB19-14", name: "Desktop Video", parent: "IAB19" },
  { code: "IAB19-15", name: "Email", parent: "IAB19" },
  { code: "IAB19-16", name: "Graphics Software", parent: "IAB19" },
  { code: "IAB19-17", name: "Home Video/DVD", parent: "IAB19" },
  { code: "IAB19-18", name: "Internet Technology", parent: "IAB19" },
  { code: "IAB19-19", name: "Java", parent: "IAB19" },
  { code: "IAB19-20", name: "JavaScript", parent: "IAB19" },
  { code: "IAB19-21", name: "Mac Support", parent: "IAB19" },
  { code: "IAB19-22", name: "MP3/MIDI", parent: "IAB19" },
  { code: "IAB19-23", name: "Net Conferencing", parent: "IAB19" },
  { code: "IAB19-24", name: "Net for Beginners", parent: "IAB19" },
  { code: "IAB19-25", name: "Network Security", parent: "IAB19" },
  { code: "IAB19-26", name: "Palmtops/PDAs", parent: "IAB19" },
  { code: "IAB19-27", name: "PC Support", parent: "IAB19" },
  { code: "IAB19-28", name: "Portable", parent: "IAB19" },
  { code: "IAB19-29", name: "Entertainment", parent: "IAB19" },
  { code: "IAB19-30", name: "Shareware/Freeware", parent: "IAB19" },
  { code: "IAB19-31", name: "Unix", parent: "IAB19" },
  { code: "IAB19-32", name: "Visual Basic", parent: "IAB19" },
  { code: "IAB19-33", name: "Web Clip Art", parent: "IAB19" },
  { code: "IAB19-34", name: "Web Design/HTML", parent: "IAB19" },
  { code: "IAB19-35", name: "Web Search", parent: "IAB19" },
  { code: "IAB19-36", name: "Windows", parent: "IAB19" },
  
  // IAB20 - Travel
  { code: "IAB20", name: "Travel", parent: null },
  { code: "IAB20-1", name: "Adventure Travel", parent: "IAB20" },
  { code: "IAB20-2", name: "Africa", parent: "IAB20" },
  { code: "IAB20-3", name: "Air Travel", parent: "IAB20" },
  { code: "IAB20-4", name: "Australia & New Zealand", parent: "IAB20" },
  { code: "IAB20-5", name: "Bed & Breakfasts", parent: "IAB20" },
  { code: "IAB20-6", name: "Budget Travel", parent: "IAB20" },
  { code: "IAB20-7", name: "Business Travel", parent: "IAB20" },
  { code: "IAB20-8", name: "By US Locale", parent: "IAB20" },
  { code: "IAB20-9", name: "Camping", parent: "IAB20" },
  { code: "IAB20-10", name: "Canada", parent: "IAB20" },
  { code: "IAB20-11", name: "Caribbean", parent: "IAB20" },
  { code: "IAB20-12", name: "Cruises", parent: "IAB20" },
  { code: "IAB20-13", name: "Eastern Europe", parent: "IAB20" },
  { code: "IAB20-14", name: "Europe", parent: "IAB20" },
  { code: "IAB20-15", name: "France", parent: "IAB20" },
  { code: "IAB20-16", name: "Greece", parent: "IAB20" },
  { code: "IAB20-17", name: "Honeymoons/Getaways", parent: "IAB20" },
  { code: "IAB20-18", name: "Hotels", parent: "IAB20" },
  { code: "IAB20-19", name: "Italy", parent: "IAB20" },
  { code: "IAB20-20", name: "Japan", parent: "IAB20" },
  { code: "IAB20-21", name: "Mexico & Central America", parent: "IAB20" },
  { code: "IAB20-22", name: "National Parks", parent: "IAB20" },
  { code: "IAB20-23", name: "South America", parent: "IAB20" },
  { code: "IAB20-24", name: "Spas", parent: "IAB20" },
  { code: "IAB20-25", name: "Theme Parks", parent: "IAB20" },
  { code: "IAB20-26", name: "Traveling with Kids", parent: "IAB20" },
  { code: "IAB20-27", name: "United Kingdom", parent: "IAB20" },
  
  // IAB21 - Real Estate
  { code: "IAB21", name: "Real Estate", parent: null },
  { code: "IAB21-1", name: "Apartments", parent: "IAB21" },
  { code: "IAB21-2", name: "Architects", parent: "IAB21" },
  { code: "IAB21-3", name: "Buying/Selling Homes", parent: "IAB21" },
  
  // IAB22 - Shopping
  { code: "IAB22", name: "Shopping", parent: null },
  { code: "IAB22-1", name: "Contests & Freebies", parent: "IAB22" },
  { code: "IAB22-2", name: "Couponing", parent: "IAB22" },
  { code: "IAB22-3", name: "Comparison", parent: "IAB22" },
  { code: "IAB22-4", name: "Engines", parent: "IAB22" },
  
  // IAB23 - Religion & Spirituality
  { code: "IAB23", name: "Religion & Spirituality", parent: null },
  { code: "IAB23-1", name: "Alternative Religions", parent: "IAB23" },
  { code: "IAB23-2", name: "Atheism/Agnosticism", parent: "IAB23" },
  { code: "IAB23-3", name: "Buddhism", parent: "IAB23" },
  { code: "IAB23-4", name: "Catholicism", parent: "IAB23" },
  { code: "IAB23-5", name: "Christianity", parent: "IAB23" },
  { code: "IAB23-6", name: "Hinduism", parent: "IAB23" },
  { code: "IAB23-7", name: "Islam", parent: "IAB23" },
  { code: "IAB23-8", name: "Judaism", parent: "IAB23" },
  { code: "IAB23-9", name: "Latter-Day Saints", parent: "IAB23" },
  { code: "IAB23-10", name: "Pagan/Wiccan", parent: "IAB23" },
  
  // IAB24 - Uncategorized
  { code: "IAB24", name: "Uncategorized", parent: null },
  
  // IAB25 - Non-Standard Content (Sensitive)
  { code: "IAB25", name: "Non-Standard Content", parent: null },
  { code: "IAB25-1", name: "Unmoderated UGC", parent: "IAB25" },
  { code: "IAB25-2", name: "Extreme Graphic/Explicit Violence", parent: "IAB25" },
  { code: "IAB25-3", name: "Pornography", parent: "IAB25" },
  { code: "IAB25-4", name: "Profane Content", parent: "IAB25" },
  { code: "IAB25-5", name: "Hate Content", parent: "IAB25" },
  { code: "IAB25-6", name: "Under Construction", parent: "IAB25" },
  { code: "IAB25-7", name: "Incentivized", parent: "IAB25" },
  
  // IAB26 - Illegal Content
  { code: "IAB26", name: "Illegal Content", parent: null },
  { code: "IAB26-1", name: "Illegal Content", parent: "IAB26" },
  { code: "IAB26-2", name: "Warez", parent: "IAB26" },
  { code: "IAB26-3", name: "Spyware/Malware", parent: "IAB26" },
  { code: "IAB26-4", name: "Copyright Infringement", parent: "IAB26" },
];

// ==================== INITIAL FORM STATE ====================

export const INITIAL_FORM_STATE = {
  // Campaign Overview
  name: "",
  iab_categories: [],
  description: "",
  primary_goal: "brand_awareness",
  kpi_type: "cpm",
  kpi_target: 5.0,
  
  // Budget & Bidding
  bidding_strategy: "manual_cpm",
  bid_pricing_type: "cpm",
  bid_floor: 0.5,
  bid_price: 2.0,
  currency: "USD",
  daily_budget: 100,
  total_budget: 3000,
  pacing_type: "even",
  // Expected rates for CPC/CPA/CPCV conversion
  expected_ctr: 0.1,  // 0.1% CTR
  expected_cvr: 1.0,  // 1% conversion rate
  expected_vcr: 50,   // 50% video completion rate
  
  // Markup (Admin/Super Admin only)
  admin_markup_percent: 0,
  super_admin_markup_percent: 0,
  
  // Inventory
  inventory_sources: ["open_exchange"],
  environments: ["web", "app"],
  
  // Geographic Targeting - Include
  geo_countries: [],
  geo_states: [],
  geo_cities: [],
  geo_pincodes: [],
  geo_regions: [],
  // Geographic Targeting - Exclude
  geo_countries_exclude: [],
  geo_states_exclude: [],
  geo_cities_exclude: [],
  geo_pincodes_exclude: [],
  geo_regions_exclude: [],
  // Lat/Long Targeting
  lat_long_targeting: false,
  lat_long_points: [],
  geo_latitude: "",
  geo_longitude: "",
  radius_km: 10,
  lat_long_type: "include",
  
  // Telecom
  telecom_operators: [],
  
  // Device Targeting
  device_types: [],
  device_makes: [],
  device_models: [],
  os_list: [],
  os_versions: [],
  browsers_include: [],
  browsers_exclude: [],
  carriers: [],
  connection_types: [],
  
  // Demographics
  age_ranges: [],
  genders: [],
  income_segments: [],
  parental_status: [],
  languages: [],
  languages_exclude: [],
  
  // Contextual Targeting
  contextual_keywords: [],
  contextual_categories: [],
  keyword_match_type: "broad",
  
  // Audience Segments
  affinity_segments: [],
  in_market_segments: [],
  
  // Placement & Viewability
  ad_placements_display_include: [],
  ad_placements_display_exclude: [],
  ad_placements_incontent_include: [],
  ad_placements_incontent_exclude: [],
  ad_placements_native_include: [],
  ad_placements_native_exclude: [],
  viewability_threshold: 50,
  exclude_non_viewable: false,
  
  // Inventory Control
  domain_allowlist: [],
  domain_blocklist: [],
  app_allowlist: [],
  app_blocklist: [],
  
  // Supply Source Control
  supply_sources_include: [],
  supply_sources_exclude: [],
  
  // Time Targeting
  time_targeting_enabled: false,
  days_of_week: [0, 1, 2, 3, 4, 5, 6],
  hours_of_day: Array.from({ length: 24 }, (_, i) => i),
  timezone: "UTC",
  
  // Brand Safety
  brand_safety_level: "standard",
  blocked_categories: [],
  blocked_keywords: [],
  blocked_domains: [],
  exclude_ugc: false,
  exclude_live_streaming: false,
  
  // Audience
  first_party_audiences: [],
  third_party_audiences: [],
  first_party_audience_input: "",
  third_party_audience_input: "",
  lookalike_enabled: false,
  lookalike_expansion: 3,
  audience_exclusions: [],
  
  // Creatives
  creative_id: "",
  creative_ids: [],
  
  // Schedule
  start_date: new Date().toISOString().split('T')[0],
  end_date: "",
  
  // Frequency Capping
  frequency_cap_enabled: false,
  frequency_cap_count: 5,
  frequency_cap_period: "day",
  frequency_cap_type: "user",
  frequency_cap_daily: 0,
  frequency_cap_lifetime: 0,
  
  // Campaign Impression Caps
  impression_cap_enabled: false,
  daily_impression_cap: 0,
  total_impression_cap: 0,
  
  // Advanced
  priority: 5,
  bid_shading_enabled: false,
  ml_prediction_enabled: false,
  spo_enabled: false,
  
  // Measurement
  conversion_tracking_enabled: false,
  conversion_pixel_id: "",
  attribution_model: "last_touch",
  click_through_window: 30,
  view_through_window: 1,
};
